import { db as defaultDb, type GentleTaskDatabase } from '@/data/db/database';
import type { NoteImage } from '@/data/db';
import { newUuid } from '@/lib/ids';
import { nowUtc } from '@/lib/clock';
import {
  removeNoteImageFile,
  storeNoteImageFile,
  type ClipboardImageSource,
} from '@/platform/note-image-files';
import { NotFoundError, markDataMutation } from './support';
import { collectNoteImageIds, parseNoteDoc } from '@/features/notes/note-content';
import { readNoteDraft } from '@/features/notes/note-draft';

export function createNoteImageRepository(db: GentleTaskDatabase = defaultDb) {
  async function add(noteId: string, source: File | ClipboardImageSource): Promise<NoteImage> {
    const note = await db.notes.get(noteId);
    if (!note || note.deletedAt !== null) throw new NotFoundError('筆記', noteId);
    const file = await storeNoteImageFile(noteId, newUuid(), source);
    const image: NoteImage = { ...file, createdAt: nowUtc() };
    try {
      await db.transaction('rw', [db.notes, db.noteImages, db.settings], async () => {
        const current = await db.notes.get(noteId);
        if (!current || current.deletedAt !== null) throw new NotFoundError('筆記', noteId);
        await db.noteImages.add(image);
        await markDataMutation(db, image.createdAt);
      });
    } catch (error) {
      await removeNoteImageFile(noteId, file.id).catch(() => {});
      throw error;
    }
    return image;
  }

  async function listUnused(): Promise<NoteImage[]> {
    const references = new Set<string>();
    for (const note of await db.notes.toArray()) {
      for (const content of [note.contentJson, readNoteDraft(note.id)?.contentJson]) {
        if (content)
          for (const id of collectNoteImageIds(parseNoteDoc(content))) references.add(id);
      }
    }
    return (await db.noteImages.toArray()).filter((image) => !references.has(image.id));
  }

  async function removeUnused(): Promise<number> {
    const unused = await listUnused();
    const results = await Promise.allSettled(
      unused.map((image) => removeNoteImageFile(image.noteId, image.id)),
    );
    const removed = unused.filter((_, index) => results[index]?.status === 'fulfilled');
    await db.transaction('rw', [db.noteImages, db.settings], async () => {
      await db.noteImages.bulkDelete(removed.map((image) => image.id));
      if (removed.length > 0) await markDataMutation(db, nowUtc());
    });
    const failure = results.find((result) => result.status === 'rejected');
    if (failure?.status === 'rejected') throw failure.reason;
    return removed.length;
  }

  /**
   * 明確刪除一張圖片前再檢查一次引用；內容已經從編輯器移除才會走到這裡。
   * 已匯出的 ZIP 是獨立檔案，不能也不應掃描使用者磁碟去改它；這會讓往後產生的
   * 完整備份不再包含這張 PNG。
   */
  async function removeIfUnused(id: string): Promise<boolean> {
    const image = (await listUnused()).find((candidate) => candidate.id === id);
    if (!image) return false;
    await removeNoteImageFile(image.noteId, image.id);
    await db.transaction('rw', [db.noteImages, db.settings], async () => {
      await db.noteImages.delete(image.id);
      await markDataMutation(db, nowUtc());
    });
    return true;
  }

  return {
    add,
    listUnused,
    removeUnused,
    removeIfUnused,
    getNote: (id: string) => db.notes.get(id),
    listFor: (noteId: string) => db.noteImages.where('noteId').equals(noteId).toArray(),
  };
}

export type NoteImageRepository = ReturnType<typeof createNoteImageRepository>;

/** Runs only after a successful restore, so retained immutable files stay intact. */
export async function removeSupersededNoteImageFiles(
  previous: NoteImage[],
  retained: NoteImage[],
): Promise<string[]> {
  const paths = new Set(retained.map((image) => image.fileName));
  const warnings: string[] = [];
  for (const image of previous) {
    if (paths.has(image.fileName)) continue;
    try {
      await removeNoteImageFile(image.noteId, image.id);
    } catch (error) {
      warnings.push(`舊圖片 ${image.fileName} 清理未完成：${String(error)}`);
    }
  }
  return warnings;
}
